import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { SEOHead } from '@/components/seo/SEOHead';
import { Shield } from 'lucide-react';

export const PrivacyPage = () => {
  const { t } = useTranslation('privacy');

  return (
    <>
      <SEOHead
        title="Privacy Policy - PeeTrack"
        description="Learn how PeeTrack protects your privacy and handles your health data securely."
        canonical="/privacy"
      />

      <div className="bg-white py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cyan-100 mb-4">
              <Shield className="h-8 w-8 text-cyan-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('title')}
            </h1>
            <p className="text-gray-600">{t('lastUpdated')}</p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('intro.title')}
              </h2>
              <p className="text-gray-700">{t('intro.content')}</p>
            </section>

            {/* 1. Information Collection */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.collection.title')}
              </h2>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {t('sections.collection.subsections.personal.title')}
              </h3>
              <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                {(t('sections.collection.subsections.personal.items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {t('sections.collection.subsections.health.title')}
              </h3>
              <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                {(t('sections.collection.subsections.health.items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {t('sections.collection.subsections.usage.title')}
              </h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                {(t('sections.collection.subsections.usage.items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            {/* 2. How We Use Your Information */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.usage.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.usage.intro')}</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                {(t('sections.usage.items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            {/* 3. Data Security */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.security.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.security.content')}</p>
              <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                {(t('sections.security.items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <p className="text-gray-700 italic">{t('sections.security.disclaimer')}</p>
            </section>

            {/* 4. Third-Party Services */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.thirdParty.title')}
              </h2>
              <p className="text-gray-700 mb-4">{t('sections.thirdParty.intro')}</p>

              <div className="space-y-3 mb-6">
                <div>
                  <strong className="text-gray-900">{t('sections.thirdParty.services.clerk.name')}:</strong>{' '}
                  <span className="text-gray-700">{t('sections.thirdParty.services.clerk.purpose')}</span>{' '}
                  <a href={t('sections.thirdParty.services.clerk.link')} className="text-cyan-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    {t('sections.thirdParty.services.clerk.link')}
                  </a>
                </div>
                <div>
                  <strong className="text-gray-900">{t('sections.thirdParty.services.pocketbase.name')}:</strong>{' '}
                  <span className="text-gray-700">{t('sections.thirdParty.services.pocketbase.purpose')}</span>{' '}
                  <a href={t('sections.thirdParty.services.pocketbase.link')} className="text-cyan-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    {t('sections.thirdParty.services.pocketbase.link')}
                  </a>
                </div>
                <div>
                  <strong className="text-gray-900">{t('sections.thirdParty.services.cloudflare.name')}:</strong>{' '}
                  <span className="text-gray-700">{t('sections.thirdParty.services.cloudflare.purpose')}</span>{' '}
                  <a href={t('sections.thirdParty.services.cloudflare.link')} className="text-cyan-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    {t('sections.thirdParty.services.cloudflare.link')}
                  </a>
                </div>
                <div>
                  <strong className="text-gray-900">{t('sections.thirdParty.services.anthropic.name')}:</strong>{' '}
                  <span className="text-gray-700">{t('sections.thirdParty.services.anthropic.purpose')}</span>{' '}
                  <a href={t('sections.thirdParty.services.anthropic.link')} className="text-cyan-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    {t('sections.thirdParty.services.anthropic.link')}
                  </a>
                </div>
              </div>

              <p className="text-gray-700">{t('sections.thirdParty.note')}</p>
            </section>

            {/* 5. Data Sharing */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.sharing.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.sharing.intro')}</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                {(t('sections.sharing.items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            {/* 6. GDPR Rights */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.rights.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.rights.intro')}</p>
              <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                {(t('sections.rights.items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <p className="text-gray-700">{t('sections.rights.contact')}</p>
            </section>

            {/* 7. Data Retention */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.retention.title')}
              </h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                {(t('sections.retention.items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            {/* 8. Children's Privacy */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.children.title')}
              </h2>
              <p className="text-gray-700">{t('sections.children.content')}</p>
            </section>

            {/* 9. Changes to Policy */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.changes.title')}
              </h2>
              <p className="text-gray-700">{t('sections.changes.content')}</p>
            </section>

            {/* 10. Contact */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.contact.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.contact.intro')}</p>
              <p className="text-gray-700 mb-2">{t('sections.contact.email')}</p>
              <p className="text-gray-700">{t('sections.contact.website')}</p>
            </section>

            {/* Back to Home */}
            <div className="text-center pt-8 border-t">
              <Link to="/" className="text-cyan-600 hover:text-cyan-700 font-semibold">
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
